import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { LanguageService } from '@aurrica-web/shared-i18n';
import { ThemeService }   from '@aurrica-web/core-data';

import { NavbarComponent }             from './navBar/navBar';
import { StatsBarComponent }           from './stats-bar/stats-bar';
import { HeroSectionComponent }        from './hero-section/hero-section';
import { AboutSectionComponent }       from './about-section/about-section';
import { FeaturesSectionComponent }    from './features-section/features-section';
import { MarketplaceSectionComponent } from './marketplace-section/marketplace-section';
import { ServicesSectionComponent }    from './services-section/services-section';
import { PartnersSectionComponent }    from './partners-section/partners-section';
import { FaqSectionComponent }         from './faq-section/faq-section';
import { CtaSectionComponent }         from './cta-section/cta-section';
import { FooterSectionComponent }      from './footer-section/footer-section';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    NavbarComponent,
    StatsBarComponent,
    HeroSectionComponent,
    AboutSectionComponent,
    FeaturesSectionComponent,
    MarketplaceSectionComponent,
    ServicesSectionComponent,
    PartnersSectionComponent,
    FaqSectionComponent,
    CtaSectionComponent,
    FooterSectionComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, AfterViewInit, OnDestroy {
  private languageService = inject(LanguageService);
  private transloco       = inject(TranslocoService);
  private destroyRef      = inject(DestroyRef);
  themeService            = inject(ThemeService);

  protected title = 'aurrica-main';

  private revealObserver?: IntersectionObserver;
  private mapObserver?: IntersectionObserver;
  private rafId?: number;
  private parallaxStarted = false;

  // Mouse parallax state — lerped each rAF tick
  private mouseTarget  = { x: 0, y: 0 };
  private mouseCurrent = { x: 0, y: 0 };

  ngOnInit(): void {
    this.languageService.init();
    if (localStorage.getItem('isDarkMode') === null) {
      this.themeService.setDarkMode(true);
    }

    // Re-bind scroll effects when translations render or the language changes.
    this.transloco.events$
      .pipe(
        filter(event => event.type === 'translationLoadSuccess'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.scheduleScrollEffectsSync());
  }

  ngAfterViewInit(): void {
    this.scheduleScrollEffectsSync();
  }

  ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.mapObserver?.disconnect();
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    window.removeEventListener('mousemove', this.onMouseMove);
  }

  private scheduleScrollEffectsSync(): void {
    requestAnimationFrame(() => {
      this.syncScrollEffects();
    });
  }

  private syncScrollEffects(): void {
    this.initRevealObserver();
    this.initMapObserver();

    if (!this.parallaxStarted) {
      this.parallaxStarted = true;
      this.initMouseParallax();
    }
  }

  // ─── Direction-aware, bidirectional reveal ──────────────────────────────────

  private initRevealObserver(): void {
    this.revealObserver?.disconnect();

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const el = entry.target;

          if (entry.isIntersecting) {
            // Entering from below (scrolling down) → top > mid-viewport
            // Entering from above (scrolling up)   → top ≤ mid-viewport
            const viewH    = entry.rootBounds?.height ?? window.innerHeight;
            const fromBelow = entry.boundingClientRect.top > viewH * 0.5;

            el.classList.remove('from-above', 'from-below');
            el.classList.add(fromBelow ? 'from-below' : 'from-above');

            // One rAF so the direction class is painted before is-visible triggers transition
            requestAnimationFrame(() => el.classList.add('is-visible'));
          } else if (entry.intersectionRatio === 0) {
            // Fully off-screen — reset so it re-animates next time
            el.classList.remove('is-visible');
          }
        });
      },
      { threshold: [0, 0.12], rootMargin: '0px 0px -48px 0px' },
    );

    document.querySelectorAll('.reveal, .reveal-left, .reveal-scale')
      .forEach(el => this.revealObserver!.observe(el));
  }

  // ─── Africa map: continuous scale driven by intersection ratio ──────────────

  private initMapObserver(): void {
    this.mapObserver?.disconnect();

    const steps      = 30;
    const thresholds = Array.from({ length: steps + 1 }, (_, i) => i / steps);

    this.mapObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const el = entry.target as HTMLElement;
          const r  = entry.intersectionRatio;
          el.style.setProperty('--map-scale',   String(0.84 + r * 0.16));   // 0.84 → 1.0
          el.style.setProperty('--map-opacity',  String(Math.min(1, r * 1.6))); // fast fade
        });
      },
      { threshold: thresholds },
    );

    document.querySelectorAll('.map-zoom-el')
      .forEach(el => this.mapObserver?.observe(el));
  }

  // ─── Mouse parallax: lerped rAF loop sets CSS vars on <html> ────────────────

  private onMouseMove = (e: MouseEvent): void => {
    const cx = window.innerWidth  / 2;
    const cy = window.innerHeight / 2;
    this.mouseTarget.x = (e.clientX - cx) / cx; // −1 … +1
    this.mouseTarget.y = (e.clientY - cy) / cy;
  };

  private initMouseParallax(): void {
    window.addEventListener('mousemove', this.onMouseMove, { passive: true });

    const tick = () => {
      // Smooth lerp toward cursor (ease = 5 % per frame)
      this.mouseCurrent.x += (this.mouseTarget.x - this.mouseCurrent.x) * 0.05;
      this.mouseCurrent.y += (this.mouseTarget.y - this.mouseCurrent.y) * 0.05;

      const root = document.documentElement;
      root.style.setProperty('--parallax-x', `${this.mouseCurrent.x * 10}px`);
      root.style.setProperty('--parallax-y', `${this.mouseCurrent.y *  7}px`);

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }
}
